import { useAuthData } from "@/context/auth-data-provider";
import { AssignmentsEnum } from "@/enum/assignments";
import { GroupsEnum } from "@/enum/groups";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import { useCallback, useMemo } from "react";
import { Assignment, User, UserPermissionGroup } from "src/@types/user";

export enum AssignmentPermission {
  CREATE = "create",
  READ = "read",
  UPDATE = "update",
  DELETE = "delete",
}

export interface AssignmentPermissionChecker {
  can: (
    assignment: AssignmentsEnum,
    permission: AssignmentPermission,
  ) => boolean;
}

export const useAssignmentPermission = (): AssignmentPermissionChecker => {
  const authData = useAuthData();
  const contextUser: User | null = (authData?.user as User) ?? null;

  const user: User | null = useMemo(
    () => contextUser ?? getUserFromLocalStorage(),
    [contextUser],
  );

  const can = useCallback(
    (
      assignment: AssignmentsEnum,
      permission: AssignmentPermission,
    ): boolean => {
      const groups: UserPermissionGroup[] = user?.user_permission_group ?? [];

      return groups.some((group: UserPermissionGroup) =>
        (group.assignments ?? []).some(
          (item: Assignment) =>
            item.assignment === assignment && item[permission] === true,
        ),
      );
    },
    [user],
  );

  return useMemo(() => ({ can }), [can]);
};

export const useHasFullAccess = (): boolean => {
  const authData = useAuthData();
  const contextUser: User | null = (authData?.user as User) ?? null;
  const user: User | null = contextUser ?? getUserFromLocalStorage();

  return (user?.user_permission_group ?? []).some(
    (group: UserPermissionGroup): boolean => group.group === GroupsEnum.ADM,
  );
};

export interface AssignmentAbilities {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

/**
 * As quatro permissões de **um** assignment, já resolvidas — a forma que a tela consome.
 *
 * Existe porque `can(assignment, permission)` obriga cada componente a repetir o assignment em toda
 * chamada, e foi assim que o hook acabou sem nenhum consumidor: o custo de usar era maior que o de
 * ignorar.
 */
export const useAssignmentAbilities = (
  assignment: AssignmentsEnum,
): AssignmentAbilities => {
  const { can } = useAssignmentPermission();

  return useMemo(
    () => ({
      canCreate: can(assignment, AssignmentPermission.CREATE),
      canRead: can(assignment, AssignmentPermission.READ),
      canUpdate: can(assignment, AssignmentPermission.UPDATE),
      canDelete: can(assignment, AssignmentPermission.DELETE),
    }),
    [can, assignment],
  );
};
